import React, { useState, useEffect, useCallback } from 'react';
import { Button, Icon, IconButton, CircularProgress } from '@material-ui/core';
import Toast from 'react-hot-toast';
import { ClienteService } from '../../../../services/ClienteService';

export const ClienteDatosContactos = ({
  clienteId,
  contactos: propContactos,
  contactoTipos: propContactoTipos,
  onChangeContactos,
  onReload,
}) => {
  const [contactos, setContactos] = useState([]);
  const [contactoTipos, setContactoTipos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Carga inicial de tipos de contacto y contactos existentes
  const loadData = useCallback(async () => {
    if (!clienteId) return;
    setLoading(true);
    try {
      // 1. Catálogo de tipos de contacto
      if (propContactoTipos && propContactoTipos.length > 0) {
        setContactoTipos(propContactoTipos);
      } else {
        const formRes = await ClienteService.obtenerFormContactos();
        if (formRes?.contacto_tipos) {
          setContactoTipos(formRes.contacto_tipos);
        }
      }

      // 2. Contactos existentes
      if (propContactos && propContactos.length > 0) {
        setContactos(propContactos);
      } else {
        const data = await ClienteService.obtenerContactos(clienteId);
        if (Array.isArray(data)) {
          setContactos(data);
        }
      }
    } catch (e) {
      console.error('Error al cargar datos de contactos', e);
    } finally {
      setLoading(false);
    }
  }, [clienteId, propContactos, propContactoTipos]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Agregar nuevo contacto (máximo 4)
  const handleAddContacto = () => {
    if (contactos.length >= 4) {
      Toast.error('El límite máximo es de 4 contactos');
      return;
    }
    const defaultTipo = contactoTipos[0]?.id || 1;
    const nuevo = {
      cco_id_cliente: clienteId,
      cco_tipo: defaultTipo,
      cco_nombre: '',
      cco_email: '',
      cco_telefono: '',
      cco_extension: '',
      id: null,
    };
    const nextContactos = [...contactos, nuevo];
    setContactos(nextContactos);
    if (onChangeContactos) {
      onChangeContactos(nextContactos);
    }
  };

  // Modificar campo de un contacto
  const handleChangeField = (index, field, value) => {
    const updated = [...contactos];
    updated[index] = { ...updated[index], [field]: value };
    setContactos(updated);
    if (onChangeContactos) {
      onChangeContactos(updated);
    }
  };

  // Eliminar contacto
  const handleDeleteContacto = async (index, contacto) => {
    if (contacto.id) {
      try {
        const ok = await ClienteService.eliminarContacto(contacto.id);
        if (ok) {
          Toast.success(`Contacto ${contacto.cco_nombre || ''} eliminado`);
          const filtered = contactos.filter((_, i) => i !== index);
          setContactos(filtered);
          if (onChangeContactos) onChangeContactos(filtered);
          if (onReload) onReload();
        } else {
          Toast.error('Error al eliminar contacto en el servidor');
        }
      } catch (e) {
        Toast.error('Error inesperado al eliminar contacto');
      }
    } else {
      const filtered = contactos.filter((_, i) => i !== index);
      setContactos(filtered);
      if (onChangeContactos) onChangeContactos(filtered);
    }
  };

  // Guardar todos los contactos
  const handleSaveContactos = async () => {
    if (contactos.length < 1) {
      Toast.error('No hay contactos para guardar');
      return;
    }

    // Validar campos
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    for (let i = 0; i < contactos.length; i++) {
      const c = contactos[i];
      if (!c.cco_nombre || c.cco_nombre.trim().length < 2) {
        Toast.error(`El nombre en el contacto #${i + 1} debe tener al menos 2 caracteres`);
        return;
      }
      if (!c.cco_email || !emailRegex.test(c.cco_email.trim())) {
        Toast.error(`El correo en el contacto #${i + 1} no tiene un formato válido`);
        return;
      }
    }

    setSaving(true);
    try {
      const payload = contactos.map((c) => ({
        ...c,
        cco_id_cliente: clienteId,
      }));
      const res = await ClienteService.guardarContactos(payload);
      if (!res.error) {
        Toast.success('Contactos guardados correctamente');
        // Recargar desde servidor para obtener IDs asignados
        const data = await ClienteService.obtenerContactos(clienteId);
        if (Array.isArray(data)) {
          setContactos(data);
          if (onChangeContactos) onChangeContactos(data);
        }
        if (onReload) onReload();
      } else {
        Toast.error(res.errMsg || 'Error al guardar contactos');
      }
    } catch (e) {
      Toast.error('Error al procesar el guardado de contactos');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-2xs border border-gray-200 p-3.5 sm:p-4 mt-3">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2.5 pb-2.5 border-b border-gray-100">
        <div>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
              <Icon fontSize="small">contacts</Icon>
            </div>
            <h3 className="text-sm font-bold text-gray-900 tracking-tight">Datos de Contactos</h3>
            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
              {contactos.length}/4
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            Personas de contacto directo para ventas, facturación, cobranza u operaciones
          </p>
        </div>

        <Button
          variant="contained"
          color="primary"
          size="small"
          className="!bg-emerald-600 hover:!bg-emerald-700 !text-white !font-semibold !text-xs !py-1 !px-3 !rounded-lg !shadow-none"
          startIcon={<Icon fontSize="small">add</Icon>}
          onClick={handleAddContacto}
          disabled={contactos.length >= 4 || saving || loading}
        >
          Agregar Contacto
        </Button>
      </div>

      {loading ? (
        <div className="py-6 flex justify-center items-center">
          <CircularProgress size={28} />
        </div>
      ) : contactos.length === 0 ? (
        <div className="text-center py-6 px-3">
          <div className="w-10 h-10 mx-auto rounded-full bg-gray-50 border border-gray-100 flex items-center justify-center text-gray-400 mb-2">
            <Icon fontSize="small">person_add</Icon>
          </div>
          <p className="text-xs font-semibold text-gray-700">Sin contactos registrados</p>
          <p className="text-[11px] text-gray-400 mt-0.5 max-w-sm mx-auto">
            Aún no se han agregado contactos para este cliente. Haz clic en "Agregar Contacto" para registrar uno.
          </p>
          <Button
            variant="outlined"
            size="small"
            className="!mt-2.5 !text-xs !text-emerald-700 !border-emerald-300 !py-0.5 !px-2.5 !capitalize"
            startIcon={<Icon fontSize="small">add</Icon>}
            onClick={handleAddContacto}
          >
            Agregar primer contacto
          </Button>
        </div>
      ) : (
        <div className="space-y-3 mt-3">
          {contactos.map((contacto, i) => (
            <div
              key={i}
              className="bg-gray-50/70 hover:bg-gray-50 rounded-lg p-3 border border-gray-200/80 transition-colors"
            >
              <div className="flex justify-between items-center mb-2">
                <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
                  Contacto #{i + 1}
                </span>
                <IconButton
                  size="small"
                  onClick={() => handleDeleteContacto(i, contacto)}
                  title="Eliminar contacto"
                  className="text-gray-400 hover:text-red-600 !p-1"
                >
                  <Icon fontSize="small">delete</Icon>
                </IconButton>
              </div>

              <div className="grid grid-cols-12 gap-2.5">
                {/* Tipo de contacto */}
                <div className="col-span-12 sm:col-span-3">
                  <label className="block text-[11px] font-semibold text-gray-600 mb-0.5">
                    Tipo de Contacto <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={contacto.cco_tipo || ''}
                    onChange={(e) => handleChangeField(i, 'cco_tipo', Number(e.target.value))}
                    className="w-full px-2.5 py-1.5 text-xs bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
                  >
                    {contactoTipos.map((tipo) => (
                      <option key={tipo.id} value={tipo.id}>
                        {tipo.cliente_contacto_tipo || tipo.tipo}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Nombre */}
                <div className="col-span-12 sm:col-span-3">
                  <label className="block text-[11px] font-semibold text-gray-600 mb-0.5">
                    Nombre Completo <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Ej. Juan Pérez"
                    value={contacto.cco_nombre || ''}
                    onChange={(e) => handleChangeField(i, 'cco_nombre', e.target.value)}
                    className="w-full px-2.5 py-1.5 text-xs bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>

                {/* Correo */}
                <div className="col-span-12 sm:col-span-3">
                  <label className="block text-[11px] font-semibold text-gray-600 mb-0.5">
                    Correo Electrónico <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    placeholder="ejemplo@correo.com"
                    value={contacto.cco_email || ''}
                    onChange={(e) => handleChangeField(i, 'cco_email', e.target.value)}
                    className="w-full px-2.5 py-1.5 text-xs bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>

                {/* Teléfono */}
                <div className="col-span-12 sm:col-span-2">
                  <label className="block text-[11px] font-semibold text-gray-600 mb-0.5">
                    Teléfono
                  </label>
                  <input
                    type="text"
                    placeholder="Ej. 3312345678"
                    value={contacto.cco_telefono || ''}
                    onChange={(e) => handleChangeField(i, 'cco_telefono', e.target.value)}
                    className="w-full px-2.5 py-1.5 text-xs bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>

                {/* Extensión */}
                <div className="col-span-12 sm:col-span-1">
                  <label className="block text-[11px] font-semibold text-gray-600 mb-0.5">Ext.</label>
                  <input
                    type="text"
                    placeholder="101"
                    value={contacto.cco_extension || ''}
                    onChange={(e) => handleChangeField(i, 'cco_extension', e.target.value)}
                    className="w-full px-2 py-1.5 text-xs bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 text-center"
                  />
                </div>
              </div>
            </div>
          ))}

          <div className="flex justify-end pt-2">
            <Button
              variant="contained"
              color="primary"
              size="small"
              className="!bg-emerald-600 hover:!bg-emerald-700 !text-white !font-semibold !text-xs !py-1 !px-4 !rounded-lg !shadow-none"
              onClick={handleSaveContactos}
              disabled={saving}
              startIcon={<Icon fontSize="small">save</Icon>}
            >
              {saving ? 'Guardando...' : 'Guardar Contactos'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClienteDatosContactos;
